// TAX2-S1-F1 (docs/build-briefs/TAX2-S1-F1-tax-rate-settings.md; DEC-304, DEC-307). The view model
// of Settings, Money and pricing, Sales tax: percent text to whole basis points, the live sum, the
// start-day presets, the plain-words refusal mapping, the history rows. No DOM, so a plain
// node:test file imports it (test/tax2-s1-f1-model.test.ts), the same reason ui/src/money.ts and
// ui/src/actorDisplay.ts are DOM-free.
//
// The screen never shows a status code, the server's own text, or the words "basis points": the
// storage unit stays in this module and the wire body, and every sentence a person reads is written
// here, from artboards A1 to A10 of design/mockups/tax2-s1-f1-tax-rate-settings.html.

import { basisPointsToPercentString, parsePercentToBasisPoints } from "./money.ts";

export type TaxPartKey =
  "state" | "county" | "transit" | "additional_county" | "city" | "special_district";

export const TAX_PART_ORDER: readonly TaxPartKey[] = [
  "state",
  "county",
  "transit",
  "additional_county",
  "city",
  "special_district",
];

/** ONBW1-S2: parts a location often does not have. A zero one is left out of the request and out of the
 * parts list, so a location without them records and sees exactly what it did before. */
export const OPTIONAL_TAX_PARTS: readonly TaxPartKey[] = ["city", "special_district"];

export const TAX_PART_LABELS: Readonly<Record<TaxPartKey, string>> = {
  state: "State",
  county: "County",
  transit: "Transit",
  additional_county: "Additional county",
  city: "City",
  special_district: "Special district",
};

export const TAX_PART_HELP: Readonly<Record<TaxPartKey, string>> = {
  state: "",
  county: "",
  transit: "",
  additional_county: "",
  city: "",
  special_district: "",
};

/** The highest total the server accepts (100%), in the wire unit. Kept in step with
 * scripts/settingsBounds.ts MAX_TAX_RATE_BASIS_POINTS by a test. */
export const MAX_TOTAL_RATE_UNITS = 10000;
/** One tap of minus or plus moves a part by a quarter of a percentage point. */
export const STEP_UNITS = 25;
export const MIN_START_DATE = "2000-01-01";
export const MAX_START_DATE = "2099-12-31";
/** The seeded set carries this start date (TAX2-S1 migration 0219). */
export const SEED_START_DATE = "1970-01-01";

// ---------------------------------------------------------------------
// Wire shapes (GET and PUT /tenants/:tenantId/tax-rates)
// ---------------------------------------------------------------------

export interface WireComponent {
  component: string;
  basisPoints: number;
}

export interface WireSet {
  rateSetId: string;
  rateSetNo: number;
  effectiveFrom: string;
  components: WireComponent[];
  totalBasisPoints: number;
  recordedBy: string;
  recordedAt: string;
  recordedByName: string | null;
}

export interface WireInEffect {
  rateSetId: string | null;
  rateSetNo: number | null;
  effectiveFrom: string | null;
  components: WireComponent[];
  totalBasisPoints: number;
  source: string;
}

export interface WireHistory {
  inEffect: WireInEffect | null;
  sets: WireSet[];
}

// ---------------------------------------------------------------------
// Percent text
// ---------------------------------------------------------------------

export type ParsedPercent = { ok: true; units: number } | { ok: false; message: string };

/** "4.75" to 475. Exact, never rounded: more than two decimals, a comma, a sign, an exponent or
 * empty text is refused in plain words. */
export function parsePercentField(raw: string): ParsedPercent {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, message: "Enter a number like 4.75, or 0.00 if there is none." };
  }
  try {
    const units = parsePercentToBasisPoints(trimmed, "This rate");
    if (units > MAX_TOTAL_RATE_UNITS) {
      return { ok: false, message: "This part is too big. A rate cannot go over 100%." };
    }
    return { ok: true, units };
  } catch {
    return {
      ok: false,
      message: "Use a number like 4.75, with at most two decimals, from 0 to 100.",
    };
  }
}

export function formatPercent(units: number): string {
  return `${basisPointsToPercentString(units)}%`;
}

/** Minus or plus on a part. Text that does not parse counts as 0.00 so a tap always does
 * something visible; the result stays between 0.00 and 100.00 and keeps two decimals. */
export function stepPercentText(raw: string, direction: 1 | -1): string {
  const parsed = parsePercentField(raw);
  const current = parsed.ok ? parsed.units : 0;
  const next = Math.min(MAX_TOTAL_RATE_UNITS, Math.max(0, current + direction * STEP_UNITS));
  return basisPointsToPercentString(next);
}

/** "+0.75%" or "-0.50%" or "no change", comparing a new total to the one in effect. */
export function describeChange(newUnits: number, currentUnits: number): string {
  const diff = newUnits - currentUnits;
  if (diff === 0) {
    return "no change";
  }
  return `${diff > 0 ? "+" : "-"}${formatPercent(Math.abs(diff))}`;
}

// ---------------------------------------------------------------------
// Dates, all as "YYYY-MM-DD" text so no device clock or zone leaks in
// ---------------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function isRealIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function parts(iso: string): { y: number; m: number; d: number } {
  return { y: Number(iso.slice(0, 4)), m: Number(iso.slice(5, 7)), d: Number(iso.slice(8, 10)) };
}

function iso(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** "2027-01-01" to "Jan 1, 2027"; with `omitYearOf` the year is dropped when it matches ("Oct 7"). */
export function formatDay(value: string, omitYearOf?: string): string {
  const p = parts(value);
  const month = MONTHS[p.m - 1] ?? "?";
  if (omitYearOf !== undefined && parts(omitYearOf).y === p.y) {
    return `${month} ${p.d}`;
  }
  return `${month} ${p.d}, ${p.y}`;
}

export function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.UTC(parts(fromIso).y, parts(fromIso).m - 1, parts(fromIso).d);
  const b = Date.UTC(parts(toIso).y, parts(toIso).m - 1, parts(toIso).d);
  return Math.round((b - a) / 86400000);
}

/** Today's calendar date in the location's business day zone, never the device zone. An unknown zone
 * name falls back to UTC, the product's own default zone. */
export function todayInZone(now: Date, timeZone: string): string {
  const format = (zone: string): string =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  try {
    return format(timeZone);
  } catch (error) {
    if (error instanceof RangeError) {
      return format("UTC");
    }
    throw error;
  }
}

/** "Oct 2, 2026 at 4:12 PM" in the location's zone. */
export function formatRecordedMoment(isoInstant: string, timeZone: string): string {
  const instant = new Date(isoInstant);
  const day = todayInZone(instant, timeZone);
  let time: string;
  try {
    time = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(instant);
  } catch (error) {
    if (!(error instanceof RangeError)) {
      throw error;
    }
    time = new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(instant);
  }
  return `${formatDay(day)} at ${time.replace(/\u202f/g, " ")}`;
}

export interface StartPreset {
  iso: string;
  label: string;
}

/** Today, the first of next month, the first of the month after, and the next January 1. */
export function startPresets(today: string): StartPreset[] {
  const p = parts(today);
  const nextMonth = p.m === 12 ? iso(p.y + 1, 1, 1) : iso(p.y, p.m + 1, 1);
  const nm = parts(nextMonth);
  const afterNext = nm.m === 12 ? iso(nm.y + 1, 1, 1) : iso(nm.y, nm.m + 1, 1);
  const nextJan = iso(p.y + 1, 1, 1);
  const out: StartPreset[] = [
    { iso: today, label: `Today, ${formatDay(today, today)}` },
    { iso: nextMonth, label: `First of next month, ${formatDay(nextMonth, today)}` },
  ];
  if (afterNext !== nextJan) {
    out.push({ iso: afterNext, label: formatDay(afterNext, today) });
  }
  if (nextJan !== nextMonth) {
    out.push({ iso: nextJan, label: formatDay(nextJan, today) });
  }
  return out;
}

// ---------------------------------------------------------------------
// The form
// ---------------------------------------------------------------------

export type FormMode = "split" | "combined";

export interface RateFormState {
  mode: FormMode;
  parts: Record<TaxPartKey, string>;
  combined: string;
  /** "YYYY-MM-DD" or "" when no day is chosen yet. */
  startDate: string;
  /** True once the person has changed something, so a missing date is flagged only then. */
  touched: boolean;
  /** True when the form was opened from "Split it into parts" on an unsplit rate. */
  splittingStartingRate: boolean;
}

export interface RateContext {
  today: string;
  /** The total in effect today, in the wire unit. Null when no rate is known. */
  inEffectUnits: number | null;
  /** The business day zone is still the default and was never confirmed (CL6-F1-F3). */
  zoneIsUnconfirmedUtc: boolean;
}

export interface FormCheck {
  partErrors: Partial<Record<TaxPartKey | "combined", string>>;
  dateError: string | null;
  /** The sum of the fields, or null when any field cannot be read. */
  totalUnits: number | null;
  overBy: number | null;
  startDate: string | null;
  isPast: boolean;
  /** One line for the result box, under the big number. */
  comparison: string;
  /** What the Save area says: why it is off, or what is about to happen. */
  saveLine: string;
  saveLabel: string;
  canSave: boolean;
  /** The result box is red (a field is wrong or the sum is over the limit). */
  bad: boolean;
}

/** The label over the big total in the form: the parts add up in split mode, a single rate is just the rate. */
export function totalLabel(mode: FormMode): string {
  return mode === "split" ? "The parts add up to" : "The rate will be";
}

export function initialFormState(
  inEffect: WireInEffect | null,
  today: string,
  splittingStartingRate: boolean,
): RateFormState {
  const zero = "0.00";
  const state: RateFormState = {
    mode: "split",
    parts: {
      state: zero,
      county: zero,
      transit: zero,
      additional_county: zero,
      city: zero,
      special_district: zero,
    },
    combined: inEffect === null ? "" : basisPointsToPercentString(inEffect.totalBasisPoints),
    startDate: splittingStartingRate ? today : "",
    touched: false,
    splittingStartingRate,
  };
  if (inEffect !== null && !splittingStartingRate) {
    for (const entry of inEffect.components) {
      if ((TAX_PART_ORDER as readonly string[]).includes(entry.component)) {
        state.parts[entry.component as TaxPartKey] = basisPointsToPercentString(entry.basisPoints);
      }
    }
  }
  return state;
}

export function checkForm(form: RateFormState, ctx: RateContext): FormCheck {
  const partErrors: FormCheck["partErrors"] = {};
  let total = 0;
  let allReadable = true;
  const keys: (TaxPartKey | "combined")[] =
    form.mode === "split" ? [...TAX_PART_ORDER] : ["combined"];
  for (const key of keys) {
    const raw = key === "combined" ? form.combined : form.parts[key];
    const parsed = parsePercentField(raw);
    if (parsed.ok) {
      total += parsed.units;
    } else {
      allReadable = false;
      partErrors[key] = parsed.message;
    }
  }
  const overBy = allReadable && total > MAX_TOTAL_RATE_UNITS ? total - MAX_TOTAL_RATE_UNITS : null;
  if (overBy !== null && form.mode === "split") {
    // Mark the part most likely to be the typo: the largest one.
    let worst: TaxPartKey = "state";
    let worstUnits = -1;
    for (const key of TAX_PART_ORDER) {
      const parsed = parsePercentField(form.parts[key]);
      if (parsed.ok && parsed.units > worstUnits) {
        worst = key;
        worstUnits = parsed.units;
      }
    }
    const guess = basisPointsToPercentString(Math.round(worstUnits / 100));
    partErrors[worst] =
      `This part is too big. The parts together cannot go over 100%. Did you mean ${guess}?`;
  }
  const totalUnits = allReadable ? total : null;

  let dateError: string | null = null;
  let startDate: string | null = null;
  if (form.startDate.length === 0) {
    if (form.touched) {
      dateError = "Pick the day the new rate starts.";
    }
  } else if (
    !isRealIsoDate(form.startDate) ||
    form.startDate < MIN_START_DATE ||
    form.startDate > MAX_START_DATE
  ) {
    dateError = "That start date is not allowed. Pick a day between 2000 and 2099.";
  } else {
    startDate = form.startDate;
  }
  const isPast = startDate !== null && startDate < ctx.today;

  const overLimit = overBy !== null;
  const bad = !allReadable || overLimit;
  // Splitting the starting rate is meant to keep the rate; parts still at zero would record 0.00%
  // by one stray tap, so that case waits for the parts to be entered.
  const noPartsYet =
    form.splittingStartingRate &&
    form.mode === "split" &&
    totalUnits === 0 &&
    (ctx.inEffectUnits ?? 0) > 0;
  const hasErrors = bad || dateError !== null || startDate === null || noPartsYet;

  let comparison: string;
  if (totalUnits === null) {
    comparison = "Fix the red items to see the total.";
  } else if (overLimit) {
    comparison = `Over by ${formatPercent(overBy ?? 0)}. A sales tax this high is almost certainly a typo.`;
  } else if (ctx.inEffectUnits === null) {
    comparison = "No rate is in effect yet.";
  } else if (form.splittingStartingRate && totalUnits === ctx.inEffectUnits) {
    comparison = `Matches today's ${formatPercent(ctx.inEffectUnits)}. Splitting only. The rate does not change.`;
  } else if (startDate !== null && startDate > ctx.today) {
    comparison =
      totalUnits === ctx.inEffectUnits
        ? `Same as today's ${formatPercent(ctx.inEffectUnits)}.`
        : `Was ${formatPercent(ctx.inEffectUnits)} today. ${totalUnits > ctx.inEffectUnits ? "Up" : "Down"} ${formatPercent(Math.abs(totalUnits - ctx.inEffectUnits))}.`;
  } else {
    comparison =
      totalUnits === ctx.inEffectUnits
        ? `Same as today's ${formatPercent(ctx.inEffectUnits)}.`
        : `Was ${formatPercent(ctx.inEffectUnits)} today. Would be ${formatPercent(totalUnits)} as soon as you record it.`;
  }

  const splitLabel = form.splittingStartingRate && form.mode === "split";
  let saveLabel = splitLabel ? "Record the parts" : "Record this rate";
  if (isPast && startDate !== null) {
    saveLabel = `Record from ${formatDay(startDate)}`;
  }

  let saveLine: string;
  if (hasErrors) {
    saveLine =
      "Fix the red items to record this rate. The parts must add up to 100% or less and the start date is required.";
    if (form.mode === "combined") {
      saveLine =
        "Fix the red items to record this rate. The rate must be 100% or less and the start date is required.";
    }
    if (!bad && dateError === null && startDate === null) {
      saveLine = "Pick the day the new rate starts to record it.";
    }
    if (noPartsYet && !bad && dateError === null && startDate !== null) {
      saveLine = `Enter the parts to record them. They should add up to today's ${formatPercent(ctx.inEffectUnits ?? 0)}.`;
    }
  } else if (totalUnits !== null && startDate !== null) {
    const day = startDate === ctx.today ? "today" : `on ${formatDay(startDate)}`;
    if (isPast) {
      saveLine = "Recording a past date. Check it twice.";
    } else if (splitLabel && ctx.inEffectUnits === totalUnits) {
      saveLine = `Records ${formatPercent(totalUnits)} as ${describeParts(form)}, starting ${day}.`;
    } else {
      saveLine =
        ctx.inEffectUnits === null
          ? `You are about to record ${formatPercent(totalUnits)} ${startDate === ctx.today ? "starting today" : `from ${formatDay(startDate)}`}.`
          : `You are about to change the rate from ${formatPercent(ctx.inEffectUnits)} to ${formatPercent(totalUnits)} ${day}.`;
    }
  } else {
    saveLine = "";
  }

  return {
    partErrors,
    dateError,
    totalUnits,
    overBy,
    startDate,
    isPast,
    comparison,
    saveLine,
    saveLabel,
    canSave: !hasErrors && totalUnits !== null && startDate !== null,
    bad,
  };
}

function describeParts(form: RateFormState): string {
  const named: string[] = [];
  for (const key of TAX_PART_ORDER) {
    const parsed = parsePercentField(form.parts[key]);
    if (parsed.ok && parsed.units > 0) {
      named.push(`${TAX_PART_LABELS[key]} ${formatPercent(parsed.units)}`);
    }
  }
  if (named.length <= 1) {
    return named[0] ?? "no parts";
  }
  return `${named.slice(0, -1).join(", ")} and ${named[named.length - 1] ?? ""}`;
}

/** The PUT body for a form that passed checkForm. Throws when it did not, so a bad form can never
 * reach the wire. Parts at 0.00 are left out (the server needs each part named once at most and
 * at least one part); a split of all zeros is refused by the check below, not sent. */
export function buildPutBody(
  form: RateFormState,
  ctx: RateContext,
  idempotencyKey: string,
): { effectiveFrom: string; components: WireComponent[]; idempotencyKey: string } {
  const check = checkForm(form, ctx);
  if (!check.canSave || check.startDate === null) {
    throw new Error("the tax rate form is not ready to record");
  }
  if (form.mode === "combined") {
    const parsed = parsePercentField(form.combined);
    if (!parsed.ok) {
      throw new Error("the combined rate cannot be read");
    }
    return {
      effectiveFrom: check.startDate,
      components: [{ component: "unsplit", basisPoints: parsed.units }],
      idempotencyKey,
    };
  }
  const components: WireComponent[] = [];
  for (const key of TAX_PART_ORDER) {
    const parsed = parsePercentField(form.parts[key]);
    if (!parsed.ok) {
      throw new Error("a part cannot be read");
    }
    if (OPTIONAL_TAX_PARTS.includes(key) && parsed.units === 0) {
      continue;
    }
    components.push({ component: key, basisPoints: parsed.units });
  }
  return { effectiveFrom: check.startDate, components, idempotencyKey };
}

/** The same request gets the same idempotency key (a dropped connection retried replays safely);
 * any change to the request gets a new one. */
export function requestSignature(body: {
  effectiveFrom: string;
  components: readonly WireComponent[];
}): string {
  return JSON.stringify([
    body.effectiveFrom,
    body.components.map((entry) => [entry.component, entry.basisPoints]),
  ]);
}

// ---------------------------------------------------------------------
// Refusals in plain words (artboard A10)
// ---------------------------------------------------------------------

export interface PlainRefusal {
  /** Bold lead, then the sentence. */
  lead: string;
  text: string;
  /** Which field to mark, when one field is at fault. */
  field: "date" | "parts" | null;
  /** The 409 offers a way to reload the section. */
  offerReload: boolean;
}

const NOT_RECORDED = "This rate was not recorded.";

/** Maps the server's answer to the words on screen. The server's message is only read to tell
 * the 400s apart; it is never shown. */
export function mapRefusal(status: number, serverMessage: string | undefined): PlainRefusal {
  const message = serverMessage ?? "";
  const make = (
    text: string,
    field: PlainRefusal["field"] = null,
    offerReload = false,
  ): PlainRefusal => ({ lead: NOT_RECORDED, text, field, offerReload });
  if (status === 0) {
    return make(
      "The server could not be reached. Nothing was recorded. Check the connection and try again.",
    );
  }
  if (status === 401) {
    return make("Your sign-in ended. Sign in again, then record the rate.");
  }
  if (status === 403) {
    return make("Your role cannot change sales tax. Ask an owner.");
  }
  if (status === 404) {
    return make("This location could not be found. Go back to Settings and try again.");
  }
  if (status === 409) {
    return make(
      "Someone already recorded a rate with these exact details but different numbers. Close this form, reload Sales tax, and check the history before trying again.",
      null,
      true,
    );
  }
  if (status === 400) {
    if (/calendar date|effectiveFrom/.test(message)) {
      return make("That start date is not allowed. Pick a day between 2000 and 2099.", "date");
    }
    if (/at least one tax component/.test(message)) {
      return make("Add at least one part before you record.");
    }
    if (/named more than once/.test(message)) {
      return make("The same part is in the list twice. Remove the extra one.", "parts");
    }
    if (/stands alone|beside named/.test(message)) {
      return make(
        "A combined rate stands alone. Use the parts, or the one combined rate, not both.",
      );
    }
    if (/together must not exceed|100%/.test(message)) {
      return make("The parts add up to more than 100%. Check each one for a typo.", "parts");
    }
    if (/whole number from 0/.test(message)) {
      return make(
        "One of the rates is not valid. Use a number like 4.75, with at most two decimals, from 0 to 100.",
        "parts",
      );
    }
  }
  return make("Something went wrong, and nothing was recorded. Try again.");
}

export function recordedMessage(totalUnits: number, startDate: string, today: string): string {
  const when = startDate <= today ? "from now on" : `on ${formatDay(startDate)}`;
  return startDate <= today
    ? `The rate is ${formatPercent(totalUnits)} ${when}.`
    : `The rate becomes ${formatPercent(totalUnits)} ${when}.`;
}

// ---------------------------------------------------------------------
// Reading the history
// ---------------------------------------------------------------------

export type HistoryStatus = "scheduled" | "in_effect" | "replaced";

export interface PartRow {
  label: string;
  text: string;
  zero: boolean;
}

export interface SetView {
  key: string;
  setNo: number;
  startsOn: string;
  isStartingRate: boolean;
  status: HistoryStatus;
  statusLabel: string;
  totalUnits: number;
  totalText: string;
  isUnsplit: boolean;
  parts: PartRow[];
  title: string;
  subtitle: string;
  footnote: string;
}

function partRows(components: readonly WireComponent[]): { rows: PartRow[]; unsplit: boolean } {
  if (components.length === 1 && components[0]?.component === "unsplit") {
    return {
      rows: [
        {
          label: "Combined rate, not split into parts",
          text: formatPercent(components[0].basisPoints),
          zero: false,
        },
      ],
      unsplit: true,
    };
  }
  const rows = TAX_PART_ORDER.filter(
    (key) =>
      !OPTIONAL_TAX_PARTS.includes(key) || components.some((entry) => entry.component === key),
  ).map((key) => {
    const units = components.find((entry) => entry.component === key)?.basisPoints ?? 0;
    return { label: TAX_PART_LABELS[key], text: formatPercent(units), zero: units === 0 };
  });
  return { rows, unsplit: false };
}

export interface RateView {
  /** The rate in effect, or null when none is known. */
  inEffect: {
    totalUnits: number;
    since: string | null;
    parts: PartRow[];
    isUnsplit: boolean;
  } | null;
  /** The next scheduled change, or null. */
  comingUp: (SetView & { inDays: number; deltaText: string }) | null;
  history: SetView[];
}

export function buildRateView(history: WireHistory, today: string, timeZone: string): RateView {
  const sets = [...history.sets].sort((a, b) => b.rateSetNo - a.rateSetNo);
  const inEffectId = history.inEffect?.rateSetId ?? null;
  // A scheduled set the newest same-date set has overridden is replaced, not scheduled.
  const newestForDate = new Map<string, number>();
  for (const set of sets) {
    const seen = newestForDate.get(set.effectiveFrom);
    if (seen === undefined || set.rateSetNo > seen) {
      newestForDate.set(set.effectiveFrom, set.rateSetNo);
    }
  }
  const views: SetView[] = sets.map((set) => {
    const isFuture = set.effectiveFrom > today;
    const overridden = newestForDate.get(set.effectiveFrom) !== set.rateSetNo;
    let status: HistoryStatus;
    if (!overridden && isFuture) {
      status = "scheduled";
    } else if (set.rateSetId === inEffectId) {
      status = "in_effect";
    } else {
      status = "replaced";
    }
    const { rows, unsplit } = partRows(set.components);
    const isStartingRate = set.effectiveFrom === SEED_START_DATE;
    const recordedDay = formatDay(todayInZone(new Date(set.recordedAt), timeZone));
    const by = set.recordedByName === null ? "" : ` by ${set.recordedByName}`;
    return {
      key: set.rateSetId,
      setNo: set.rateSetNo,
      startsOn: set.effectiveFrom,
      isStartingRate,
      status,
      statusLabel:
        status === "scheduled" ? "Scheduled" : status === "in_effect" ? "In effect" : "Replaced",
      totalUnits: set.totalBasisPoints,
      totalText: formatPercent(set.totalBasisPoints),
      isUnsplit: unsplit,
      parts: rows,
      title: isStartingRate ? "The starting rate" : `Starts ${formatDay(set.effectiveFrom)}`,
      subtitle: isStartingRate
        ? `Set ${set.rateSetNo}, before parts were tracked`
        : `Set ${set.rateSetNo}, recorded ${recordedDay}${by}`,
      footnote: isStartingRate
        ? "Written by the system when tax parts were introduced"
        : `Recorded${set.recordedByName === null ? "" : ` by ${set.recordedByName},`} ${formatRecordedMoment(set.recordedAt, timeZone)}`,
    };
  });

  const scheduled = views
    .filter((view) => view.status === "scheduled")
    .sort((a, b) => (a.startsOn < b.startsOn ? -1 : 1));
  const next = scheduled[0];
  const currentUnits = history.inEffect?.totalBasisPoints ?? null;
  let comingUp: RateView["comingUp"] = null;
  if (next !== undefined) {
    const source = sets.find((set) => set.rateSetId === next.key);
    comingUp = {
      ...next,
      inDays: daysBetween(today, next.startsOn),
      deltaText: currentUnits === null ? "" : describeChange(next.totalUnits, currentUnits),
      footnote: `${source === undefined || source.recordedByName === null ? "Recorded" : `Recorded by ${source.recordedByName}`} on ${
        source === undefined ? "" : formatDay(todayInZone(new Date(source.recordedAt), timeZone))
      }. A recorded rate cannot be edited or removed. To fix it, record a new rate with the same start date; the newest one wins.`,
    };
  }

  let inEffect: RateView["inEffect"] = null;
  if (history.inEffect !== null) {
    const { rows, unsplit } = partRows(history.inEffect.components);
    inEffect = {
      totalUnits: history.inEffect.totalBasisPoints,
      since:
        history.inEffect.effectiveFrom === null ||
        history.inEffect.effectiveFrom === SEED_START_DATE
          ? null
          : history.inEffect.effectiveFrom,
      parts: rows,
      isUnsplit: unsplit,
    };
  }
  return { inEffect, comingUp, history: views };
}

export function daysText(days: number): string {
  if (days === 1) {
    return "tomorrow";
  }
  return `in ${days} days`;
}

// ---------------------------------------------------------------------
// The past date warning (artboard A4, DEC-307 ruling 3)
// ---------------------------------------------------------------------

/** The warning for a start date that has already passed, or null for today and the future. A date
 * before the rate now in effect cannot change today's rate (the rate in effect is the newest start
 * day at or before today), so it says what it does change instead of promising a new rate. */
export function pastDateWarning(
  startDate: string | null,
  today: string,
  inEffectFrom: string | null,
): { lead: string; text: string } | null {
  if (startDate === null || startDate >= today) {
    return null;
  }
  const keeps = "Payments already taken keep the tax they were charged.";
  if (inEffectFrom !== null && inEffectFrom !== SEED_START_DATE && startDate < inEffectFrom) {
    return {
      lead: "This date is before the rate now in effect.",
      text: `Today's rate does not change, because the rate in effect started on ${formatDay(inEffectFrom)}. Reports for the days from ${formatDay(startDate)} up to the next recorded rate will show this rate. ${keeps} Only record a past date to correct the rate.`,
    };
  }
  return {
    lead: "This date has already passed.",
    text: `From now on, every new payment uses the new rate. ${keeps} Reports for those days will show the rate that was in effect then. Only record a past date to correct the rate.`,
  };
}
